import { Component, effect, inject, OnChanges, untracked, input } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { Observable, of, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  WarningDialogComponent,
  WarningDialogData,
} from '../../../../components/warning-dialog/warning-dialog.component';
import { DotsLoaderComponent } from '../../../../shared/components/dots-loader/dots-loader.component';
import { TextOverflowEllipsisDirective } from '../../../../shared/directives/text-overflow-ellipsis.directive';
import { UserDetails } from '../../../auth0/models';
import { AuthApiService } from '../../../auth0/services/auth-api.service';
import { Auth0Store } from '../../../auth0/store';

@Component({
  selector: 'gnx-organization-info',
  templateUrl: './organization-info.component.html',
  styleUrls: ['./organization-info.component.scss'],
  imports: [ReactiveFormsModule, DotsLoaderComponent, MatTooltip, TextOverflowEllipsisDirective],
})
export class OrganizationInfoComponent implements OnChanges {
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private service = inject(AuthApiService);

  protected auth0Store = inject(Auth0Store);
  readonly data = input<UserDetails>(undefined);
  readonly isAdmin = input<boolean>(undefined);

  orgForm: FormGroup;
  formSubmitted: boolean;
  editMode = false;
  uniqueOrgViolationMsg: string;

  private prevUpdateOrgInProgress: boolean;

  private updateOrgEffect = effect(() => {
    const inProgress = this.auth0Store.updateOrgInProgress();
    if (this.prevUpdateOrgInProgress && !inProgress) {
      untracked(() => {
        this.editMode = false;
        this.orgForm.get('orgName')?.disable();
      });
    }
    this.prevUpdateOrgInProgress = inProgress;
  });

  ngOnChanges() {
    this.orgForm = this.fb.group({
      orgName: [
        { value: this.data().organization.name, disabled: true },
        [Validators.required],
        this.orgNameAvailabilityValidator.bind(this),
      ],
    });
  }

  onEdit(): void {
    this.editMode = true;
    this.orgForm.get('orgName')?.enable();
  }

  onCancelEdit(): void {
    this.editMode = false;
    this.orgForm.reset({ orgName: this.data().organization.name });
    this.orgForm.get('orgName')?.disable();
  }

  onSaveWithConfirmation(): void {
    this.dialog.open(WarningDialogComponent, {
      width: '570px',
      height: '430px',
      backdropClass: 'popup-backdrop',
      panelClass: 'popup-panel',
      hasBackdrop: true,
      data: {
        title: 'Change Organization Name',
        description:
          'Are you sure you want to change the organization name? This change will be displayed for all users in your organization.',
        yesButtonLabel: 'Yes, Change Name',
        cancelButtonLabel: 'No, Go Back',
        hideTopSection: false,
        reverseButtonsOrder: false,
        yesButtonCallback: () => this.onSubmit(),
      } as WarningDialogData,
    });
  }

  onSubmit(): void {
    this.formSubmitted = true;
    this.auth0Store.updateOrgInfo({ name: this.orgForm.get('orgName').value });
  }

  get f() {
    return this.orgForm.controls;
  }

  orgNameAvailabilityValidator(control: AbstractControl): Observable<ValidationErrors | null> {
    return control.value !== this.data().organization.name
      ? timer(500).pipe(
          switchMap(() => this.service.checkOrganizationAvailability(control.value)),
          map((result) => {
            if (!result.available) {
              this.uniqueOrgViolationMsg = result.error;
              return { uniqueOrgViolation: true };
            }
            return null;
          }),
        )
      : of(null);
  }
}
